import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Column } from 'primereact/column'

import { PanelContainer } from '../../../../components/Panel'
import { InvoiceItem } from '../../../../data/models'
import { getConfigTranslation } from '../../../../utils/helper/getConfigTranslation'
import {
  isRowSelectable,
  rowClassName,
} from '../../../../utils/helper/isRowSelectable'
import { ConfigurableTable, convertOrderItems } from '../../../../utils/wrapper'
import { useConfigContext } from '../../../ui-config/context'
import { useAllInvoiceItems, useInvoiceById } from '../../data'
import { EditInvoiceItem, InfoInvoiceItem } from './dialogs'
import { EditInvoiceItemAllowanceCharge } from './dialogs/EditInvoiceItem/EditInvoiceItemAllowanceCharge'
import { InfoInvoiceItemAllowanceCharge } from './dialogs/InfoInvoiceItem/InfoInvoiceItemAllowanceCharge'

type Props = {
  invoiceId: string
}

export const InvoiceItems: React.FC<Props> = ({ invoiceId }) => {
  const { t } = useTranslation()
  const { uiConfig: config } = useConfigContext()

  const { data: invoice } = useInvoiceById(invoiceId)
  const { data: invoiceItems, isLoading } = useAllInvoiceItems(invoiceId)

  const [infoMode, setInfoMode] = useState(false)
  const [editMode, setEditMode] = useState(false)

  const [displayEditItem, setDisplayEditItem] = useState(false)
  const [displayEditItemAllowanceCharge, setDisplayEditItemAllowanceCharge] =
    useState(false)
  const [idEditItemAllowanceCharge, setIdEditItemAllowanceCharge] = useState('')

  const [displayInfoItem, setDisplayInfoItem] = useState(false)
  const [displayInfoItemAllowanceCharge, setDisplayInfoItemAllowanceCharge] =
    useState(false)
  const [idInfoItemAllowanceCharge, setIdInfoItemAllowanceCharge] = useState('')

  const [displayRejectItem, setDisplayRejectItem] = useState(false)

  const [itemId, setItemId] = useState('')

  const [editActiveTabViewPanelIndex, setEditActiveTabViewPanelIndex] =
    useState<number>(0)

  const [infoActiveTabViewPanelIndex, setInfoActiveTabViewPanelIndex] =
    useState<number>(0)

  // get items for rejecting or accepting
  const [selectedItems, setSelectedItems] = useState<InvoiceItem[] | null>()

  const headerTitle = t('invoice.invoiceItems.table.title')

  //  action template
  const actionBodyTemplate = (rowData: any) => {
    return (
      <>
        <div className="flex justify-content-end flex-nowrap">
          <Button
            icon="pi pi-info-circle"
            className="p-button-rounded p-button-text mr-2"
            onClick={() => {
              setItemId(rowData.Id)
              setDisplayInfoItem(true)
            }}
            visible={config.invoice.infoInvoiceItem.enabled !== false}
          />

          <Button
            icon="pi pi-pencil"
            className="p-button-rounded p-button-text"
            onClick={() => {
              setItemId(rowData.Id)
              setDisplayEditItem(true)
            }}
            visible={config.invoice.editInvoiceItem.enabled !== false}
          />
        </div>
      </>
    )
  }

  const buttons = (
    <Column key="buttons" body={actionBodyTemplate} align="right"></Column>
  )

  return (
    <PanelContainer
      header={
        getConfigTranslation(config.invoice.invoiceItems.title) ??
        t('invoice.invoiceItems.title')
      }
      id="invoice-position"
      visible={config.invoice.invoiceItems.enabled}
    >
      <>
        <ConfigurableTable
          config={config.invoice.invoiceItems.table}
          data={convertOrderItems(invoiceItems)}
          buttons={buttons}
          headerTitle={headerTitle}
          checkboxen={true}
          selected={selectedItems}
          setSelected={setSelectedItems}
          isDataSelectable={isRowSelectable}
          rowClassName={rowClassName}
          isLoading={isLoading}
          stateKey=""
        />

        <EditInvoiceItem
          visible={displayEditItem}
          setVisible={setDisplayEditItem}
          setDisplayEditItemAllowanceCharge={setDisplayEditItemAllowanceCharge}
          setIdEditItemAllowanceCharge={setIdEditItemAllowanceCharge}
          setDisplayInfoItemAllowanceCharge={setDisplayInfoItemAllowanceCharge}
          setIdInfoItemAllowanceCharge={setIdInfoItemAllowanceCharge}
          invoiceId={invoiceId}
          itemId={itemId}
          setDisplayRejectItem={setDisplayRejectItem}
          setSelectedItems={setSelectedItems}
          currency={invoice?.Payload.OrderCurrency!}
          setEditModus={setEditMode}
          editActiveTabViewPanelIndex={editActiveTabViewPanelIndex}
          setEditActiveTabViewPanelIndex={setEditActiveTabViewPanelIndex}
          setInfoActiveTabViewPanelIndex={setInfoActiveTabViewPanelIndex}
        />

        {itemId && idEditItemAllowanceCharge && (
          <EditInvoiceItemAllowanceCharge
            setDisplayEditItem={setDisplayEditItem}
            visible={displayEditItemAllowanceCharge}
            setVisible={setDisplayEditItemAllowanceCharge}
            invoiceId={invoiceId}
            itemId={itemId}
            id={idEditItemAllowanceCharge}
          />
        )}

        <InfoInvoiceItem
          visible={displayInfoItem}
          setVisible={setDisplayInfoItem}
          setDisplayInfoItemAllowanceCharge={setDisplayInfoItemAllowanceCharge}
          setIdInfoItemAllowanceCharge={setIdInfoItemAllowanceCharge}
          invoiceId={invoiceId}
          itemId={itemId}
          currency={invoice?.Payload.OrderCurrency!}
          setInfoModus={setInfoMode}
          infoActiveTabViewPanelIndex={infoActiveTabViewPanelIndex}
          setInfoActiveTabViewPanelIndex={setInfoActiveTabViewPanelIndex}
        />

        {itemId && idInfoItemAllowanceCharge && (
          <InfoInvoiceItemAllowanceCharge
            infoMode={infoMode}
            setInfoMode={setInfoMode}
            editMode={editMode}
            setEditMode={setEditMode}
            setDisplayEditItem={setDisplayEditItem}
            setDisplayInfoItem={setDisplayInfoItem}
            visible={displayInfoItemAllowanceCharge}
            setVisible={setDisplayInfoItemAllowanceCharge}
            invoiceId={invoiceId}
            itemId={itemId}
            id={idInfoItemAllowanceCharge}
            setIdInfoItemAllowanceCharge={setIdInfoItemAllowanceCharge}
            setInfoActiveTabViewPanelIndex={setInfoActiveTabViewPanelIndex}
            setEditActiveTabViewPanelIndex={setEditActiveTabViewPanelIndex}
          />
        )}
      </>
    </PanelContainer>
  )
}
